/**
 * 발송 불가(바운스) 이메일 안내 팝업.
 *
 * 소셜 로그인(특히 네이버)은 이메일 실제 수신 가능 여부를 검증하지 않아, 폐쇄된 옛 주소
 * (korea.com·hanmail 등)로 가입된 계정이 있다. 그런 계정에 안내 메일이 반송되면 서버가
 * users.email_undeliverable_at 을 찍는다(/auth/me → email_undeliverable=true).
 *
 * 그 사용자가 다음 로그인 하면 이 팝업이 떠서 "받을 수 있는 새 이메일"을 요구한다.
 * 닫기 없이(블로킹) — 안내·법정 알림 메일이 전달돼야 서비스 가치가 성립하기 때문.
 *
 * ★원인별로 다른 화면을 보여준다★ (2026-08-23)
 *   '메일함 가득'(4.2.2·5.2.2)은 주소가 멀쩡한데 받을 자리가 없는 것이다. 여기에 대고
 *   "주소가 전달 불가하니 바꾸세요" 라고만 하면 사실과 다른 안내다 — 실제로 결제 당일
 *   유료 전환한 고객이 이 상태였다. 이 경우엔 '메일함 정리' 를 먼저 제시하고, 다른 주소는
 *   대안으로 둔다. 유료 사용자에게는 못 받고 있는 것에 '결제 영수증' 이 포함됨을 명시한다.
 */
import { useState } from 'react'
import { FiMail, FiAlertTriangle } from 'react-icons/fi'
import { api } from '../../lib/api'
import { useAuthStore } from '../../store/authStore'
import { toast } from '../../hooks/useToast'
import Button from '../ui/Button'
import EmailDomainNotice from './EmailDomainNotice'
import type { User } from '../../types/user'

export default function UndeliverableEmailModal() {
  const { user, setUser } = useAuthStore()
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  // 발송 불가로 표시된 계정에만 뜬다.
  if (!user || !user.email_undeliverable) return null

  // 메일함 용량 초과 — 서버의 MAILBOX_FULL_CODES 와 같은 목록을 본다.
  const mailboxFull = user.email_bounce_code === '4.2.2' || user.email_bounce_code === '5.2.2'
  const paid = !!user.is_paid

  const submit = async () => {
    const v = email.trim()
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)) {
      toast.error('올바른 이메일 주소를 입력해 주세요.')
      return
    }
    setBusy(true)
    try {
      const { data } = await api.post<User>('/auth/me/change-email', { new_email: v })
      setUser(data)             // email_undeliverable=false 로 갱신 → 팝업 닫힘
      setDone(true)
    } catch (e) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg || '이메일 변경에 실패했습니다. 다시 시도해 주세요.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/55 p-4">
      <div className="w-full max-w-md rounded-2xl bg-surface-card border border-surface-elevated/60 shadow-xl p-6">
        {!done ? (
          <>
            <div className="flex items-center gap-2.5 mb-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-amber-500/15 text-amber-600">
                <FiAlertTriangle size={18} />
              </span>
              <h2 className="text-base font-bold text-ink">
                {mailboxFull ? '메일함이 가득 차 메일이 반송되고 있어요' : '이메일이 전달되지 않고 있어요'}
              </h2>
            </div>
            {mailboxFull ? (
              <p className="text-sm text-ink-muted leading-relaxed mb-1">
                <b className="text-ink">{user.email}</b> 메일함의 저장 용량이 가득 차서, 저희가 보낸
                메일이 <b className="text-amber-600">되돌아오고</b> 있습니다. 주소가 잘못된 것은 아닙니다.
                {paid
                  ? ' 지금은 결제 영수증과 법정 의무·안전진단 안내 메일이 전달되지 않습니다.'
                  : ' 지금은 법정 의무·안전진단·환급 안내 메일이 전달되지 않습니다.'}
              </p>
            ) : (
              <p className="text-sm text-ink-muted leading-relaxed mb-1">
                현재 등록된 주소 <b className="text-ink">{user.email}</b> 로 보낸 안내 메일이
                <b className="text-amber-600"> 반송</b>되었습니다. 이 주소로는 안전진단·법정 의무·
                환급 안내를 받아보실 수 없습니다.
              </p>
            )}
            {/* 반송 사유(088) — '없는 주소'와 '카카오 휴면 잠금'은 해결 방법이 다르다. */}
            {user.email_bounce_reason && (
              <p className="mb-3 rounded-lg border border-surface-elevated/60 bg-surface-base px-3 py-2 text-xs leading-relaxed text-ink-muted">
                <b className="text-ink">확인된 원인 · </b>{user.email_bounce_reason}
              </p>
            )}
            {mailboxFull ? (
              <p className="text-sm text-ink-muted leading-relaxed mb-4">
                <b className="text-ink">메일함을 정리</b>하시면 다시 받아보실 수 있습니다.
                바로 받아보시려면 아래에 <b className="text-ink">받을 수 있는 다른 주소</b>를 입력해 주세요.
              </p>
            ) : (
              <p className="text-sm text-ink-muted leading-relaxed mb-4">
                메일을 받을 수 있는 <b className="text-ink">다른 이메일 주소</b>를 입력해 주세요.
              </p>
            )}
            <label className="block text-xs font-medium text-ink-muted mb-1">새 이메일 주소</label>
            <div className="flex items-center gap-2 rounded-lg border border-surface-elevated bg-surface-base px-3 py-2 mb-2">
              <FiMail size={15} className="text-ink-faint shrink-0" />
              <input
                type="email" value={email} autoFocus
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !busy) submit() }}
                placeholder="name@example.com"
                className="w-full bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
              />
            </div>
            <EmailDomainNotice email={email} onApplySuggestion={setEmail} className="mb-2" />
            <div className="h-2" />
            <Button className="w-full" onClick={submit} disabled={busy}>
              {busy ? '변경 중…' : '이 주소로 변경하기'}
            </Button>
            <p className="mt-3 text-[11px] text-ink-faint leading-relaxed">
              변경하시면 새 주소로 인증 메일을 보내드립니다. 로그인 자체는 기존 방식(소셜 로그인 포함)
              그대로 유지됩니다.
            </p>
          </>
        ) : (
          <div className="text-center py-2">
            <div className="text-3xl mb-2">✅</div>
            <h2 className="text-base font-bold text-ink mb-1">변경되었습니다</h2>
            <p className="text-sm text-ink-muted leading-relaxed">
              <b className="text-ink">{email.trim()}</b> 로 인증 메일을 보내드렸어요.
              메일함에서 인증을 완료해 주세요.
            </p>
            <Button className="mt-4 w-full" onClick={() => setDone(false)}>확인</Button>
          </div>
        )}
      </div>
    </div>
  )
}
